import { useEffect, useRef, useState } from 'react';
import { StanModal } from '@/components';
import BoxIcon from '@/assets/icons/ic-box.svg';
import { createIconElement } from '@/lib';

interface ExportCsvModalProps<T> {
    isOpen: boolean;
    totalPages: number;
    filename: string;
    onClose: () => void;
    onDataFetched: (data: T[]) => void;
    fetchPageData: (page: number) => Promise<T[]>;
}

/**
 * Modal for exporting recipients data to CSV
 * Shows progress while fetching data in batches
 */
export const ExportCsvModal = <T,>({
    isOpen,
    totalPages,
    filename,
    onClose,
    onDataFetched,
    fetchPageData,
}: ExportCsvModalProps<T>) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [hasStarted, setHasStarted] = useState(false);
    const isExportingRef = useRef(false);

    useEffect(() => {
        if (isOpen && !hasStarted) {
            // Reset state when modal opens
            setCurrentPage(0);
            isExportingRef.current = true;
            setHasStarted(true);

            // Start export on next tick to ensure modal is rendered
            setTimeout(() => {
                startExport();
            }, 0);
        } else if (!isOpen && hasStarted) {
            // Reset when modal closes
            setHasStarted(false);
            isExportingRef.current = false;
        }
    }, [isOpen, hasStarted]);

    const startExport = async () => {
        if (totalPages === 0) {
            handleClose();
            return;
        }

        try {
            const allData = await fetchAllPages();
            onDataFetched(allData);

            // Small delay before closing to show completion
            setTimeout(() => {
                handleClose();
            }, 500);
        } catch (error) {
            console.error('Export failed:', error);
            handleClose();
        }
    };

    const fetchAllPages = async (): Promise<T[]> => {
        const CONCURRENT_REQUESTS = 5;
        let allData: T[] = [];

        const fetchBatch = async (startIdx: number): Promise<void> => {
            if (startIdx >= totalPages || !isExportingRef.current) {
                return;
            }

            const batchPages = Array.from(
                { length: Math.min(CONCURRENT_REQUESTS, totalPages - startIdx) },
                (_, i) => startIdx + i + 1
            );

            const dataPromises = batchPages.map(page => fetchPageData(page));
            const batchData = await Promise.all(dataPromises);

            allData = allData.concat(...batchData);
            setCurrentPage(prev => prev + batchPages.length);

            await fetchBatch(startIdx + CONCURRENT_REQUESTS);
        };

        await fetchBatch(0);
        return allData;
    };

    const handleClose = () => {
        isExportingRef.current = false;
        setCurrentPage(0);
        onClose();
    };

    return (
        <StanModal
            id='export-csv-modal'
            isOpen={isOpen}
            title='Exporting Recipients'
            size='sm'
            hideCloseButton
            nonDismissible
            onClose={handleClose}
        >
            <div className='flex flex-col items-center justify-center py-6'>
                <div className='flex items-center justify-center gap-4 py-10'>
                    {createIconElement(BoxIcon, { className: 'size-20 animate-bounce opacity-50' })}
                    {createIconElement(BoxIcon, { className: 'size-28 animate-bounce opacity-50' })}
                    {createIconElement(BoxIcon, { className: 'size-16 animate-bounce opacity-50' })}
                </div>

                <p className='text-caption-1-regular text-gray mb-4'>{filename}</p>

                <div className='bg-neutral-subtle h-2 w-full rounded-full'>
                    <div
                        className='bg-brand-main h-2 rounded-full transition-all duration-300'
                        style={{ width: `${(currentPage / totalPages) * 100}%` }}
                    />
                </div>

                <p className='text-caption-2-regular text-gray mt-2'>Exporting...</p>
            </div>
        </StanModal>
    );
};
